import React, { useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as auth from "../redux/AuthRedux";
import { toAbsoluteUrl } from "../../../../_start/helpers";
import { db, authFirebase } from "../../../../config/firebase";
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, AuthError } from "firebase/auth";
import { doc, collection, getDoc, setDoc } from "firebase/firestore";

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Wrong email format")
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Email is required"),
  password: Yup.string()
    .min(3, "Minimum 3 symbols")
    .max(50, "Maximum 50 symbols")
    .required("Password is required"),
});

const initialValues = {
  email: "",
  password: "",
};

/**
 * Maps Firebase auth error codes to user-friendly messages
 */
function getAuthErrorMessage(errorCode: string): string {
  switch (errorCode) {
    case "auth/invalid-email":
      return "Invalid email address format.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/user-not-found":
      return "No account found with this email.";
    case "auth/wrong-password":
      return "Incorrect password.";
    case "auth/invalid-credential":
      return "Invalid email or password.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Please try again later.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection.";
    case "auth/popup-closed-by-user":
      return "Sign-in was cancelled.";
    default:
      return "An error occurred during sign in. Please try again.";
  }
}

export function Login() {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  // const userID = store.getState().auth.user.uid;
  // const userName= store.getState().auth.user?.firstname;

  const addUserFirebase = async (userID: string, userName: string) => {
    const userRef = doc(db, "users", userID);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      await setDoc(doc(collection(db, 'users'), userID), {
        firstName: userName,
      });
    }
  };
  

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        const userCredential = await signInWithEmailAndPassword(
          authFirebase,
          values.email,
          values.password
        );
        const user = userCredential.user;
        const [firstPart = "", secondPart = ""] = (user.displayName ?? "").split(" ");
        const emailLocalPart = values.email.split("@")[0] ?? "";
        const userModel = {
          id: user.uid,
          uid: user.uid,
          email: user.email ?? "",
          firstname: firstPart || emailLocalPart,
          lastname: secondPart,
        };
        dispatch(auth.actions.fulfillUser({ user: userModel }));
        await addUserFirebase(user.uid, firstPart || emailLocalPart);
        setLoading(false);
      } catch (error) {
        const authError = error as AuthError;
        setLoading(false);
        setSubmitting(false);
        setStatus(getAuthErrorMessage(authError.code));
      }
    },
  });

  const googleSignIn = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(authFirebase, provider);
      const user = result.user;

      const userModel = {
        id: user.uid,
        uid: user.uid,
        email: user.email || "",
        firstname: user.displayName?.split(" ")[0] || "",
        lastname: user.displayName?.split(" ")[1] || "",
      };

      dispatch(auth.actions.fulfillUser({ user: userModel }));
      await addUserFirebase(user.uid, user.displayName || "Unknown");
    } catch (error) {
      const authError = error as AuthError;
      formik.setStatus(getAuthErrorMessage(authError.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      className="form w-100"
      onSubmit={formik.handleSubmit}
      noValidate
      id="kt_login_signin_form"
    >
      {/* begin::Title */}
      <div className="pb-lg-15">
        <h3 className="fw-bolder text-dark display-6">Welcome to Business Foundry</h3>
        <div className="text-muted fs-5">
          New Here?{" "}
          <Link
            to="/auth/registration"
            className="text-primary fw-bolder"
            id="kt_login_signin_form_singup_button"
          >
            Create Account
          </Link>
        </div>
      </div>
      {/* begin::Title */}

      {formik.status && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formik.status}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className="v-row mb-10 fv-plugins-icon-container">
        <label className="form-label fs-6 fw-bolder text-dark">Email</label>
        <input
          placeholder="Email"
          {...formik.getFieldProps("email")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            { "is-invalid": formik.touched.email && formik.errors.email },
            {
              "is-valid": formik.touched.email && !formik.errors.email,
            }
          )}
          type="email"
          name="email"
          autoComplete="off"
        />
        {formik.touched.email && formik.errors.email && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">{formik.errors.email}</div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="fv-row mb-10 fv-plugins-icon-container">
        <div className="d-flex justify-content-between mt-n5">
          <label className="form-label fs-6 fw-bolder text-dark pt-5">
            Password
          </label>

          <Link
            to="/auth/forgot-password"
            className="text-primary fs-6 fw-bolder text-hover-primary pt-5"
            id="kt_login_signin_form_password_reset_button"
          >
            Forgot Password ?
          </Link>
        </div>
        <input
          type="password"
          autoComplete="off"
          {...formik.getFieldProps("password")}
          className={clsx(
            "form-control form-control-lg form-control-solid",
            {
              "is-invalid": formik.touched.password && formik.errors.password,
            },
            {
              "is-valid": formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">{formik.errors.password}</div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className="pb-lg-0 pb-5">
        <button
          type="submit"
          id="kt_login_signin_form_submit_button"
          className="btn btn-primary fw-bolder fs-6 px-8 py-4 my-3 me-3"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className="indicator-label">Sign In</span>}
          {loading && (
            <span className="indicator-progress" style={{ display: "block" }}>
              Please wait...{" "}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
        <button
          onClick={googleSignIn}
          type="button"
          className="btn btn-light-primary fw-bolder px-8 py-4 my-3 fs-6 mr-3"
        >
          <img
            src={toAbsoluteUrl("/media/svg/brand-logos/google-icon.svg")}
            className="w-20px h-20px me-3"
            alt="Google logo"
          />
          Sign in with Google
        </button>
      </div>
      {/* end::Action */}
    </form>
  );
}